// Git Status Tool - Gets the status of files in the Git repository
// Extends WorkspaceTool - has access to appType, fs, and git
// Returns modified, added, deleted, staged, and untracked files
// Used to populate the source control panel with file changes

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitStatusTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'gitStatusTool';
  }
  /**
   * Get the status of files in the Git repository
   * @returns {Promise<Object>} Result with file status information
   */
  async gitStatus() {
    try {
      // Route based on app type
      if (this.appType === "browser") {
        return await this.gitStatusForBrowser();
      }

      if (this.appType === "local") {
        return await this.gitStatusForLocal();
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for gitStatus`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitstatus|error",
          color1: "red",
        },
        "Failed to get git status",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Execute git status for browser apps using isomorphic-git
   */
  async gitStatusForBrowser() {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const dir = "/files";

    try {
      // Check if .git exists
      const gitExists = await this.checkGitExists(fs);
      if (!gitExists) {
        // No git repo - return empty status
        return this.returnSuccess(
          {
            hasGit: false,
            stagedFiles: [],
            unstagedFiles: [],
            untrackedFiles: [],
            allFiles: [],
          },
          "No git repository found"
        );
      }

      // Get status matrix from isomorphic-git
      const statusMatrix = await this.git.statusMatrix({
        fs: this.fs,
        dir,
        filter: (f) => {
          // Filter out .git directory and .revolt directory
          return (
            !f.startsWith(".git/") &&
            f !== ".git" &&
            !f.startsWith(".revolt/") &&
            f !== ".revolt"
          );
        },
      });

      // Parse status matrix into categorized files
      // Status codes (isomorphic-git):
      // HEAD Status: 0 = absent, 1 = present
      // Workdir Status: 0 = absent (deleted), 1 = identical to HEAD, 2 = different from HEAD
      // Stage Status: 0 = absent, 1 = identical to HEAD, 2 = identical to Workdir, 3 = different from Workdir
      const stagedFiles = [];
      const unstagedFiles = [];
      const untrackedFiles = [];

      for (const [
        filepath,
        headStatus,
        workdirStatus,
        stageStatus,
      ] of statusMatrix) {
        // Skip .git and .revolt directories
        if (
          filepath.startsWith(".git/") ||
          filepath === ".git" ||
          filepath.startsWith(".revolt/") ||
          filepath === ".revolt"
        ) {
          continue;
        }

        // Determine file status
        // headStatus: 0=absent, 1=present (in HEAD)
        // workdirStatus: 0=absent (deleted), 1=identical to HEAD, 2=different from HEAD (in working dir)
        // stageStatus: 0=absent, 1=identical to HEAD, 2=identical to Workdir, 3=different from Workdir (in index)

        // IMPORTANT: A file can be BOTH staged AND unstaged simultaneously!
        // This happens when you stage some changes, then make more changes to the same file.
        // We need to check both conditions independently (not using else if).

        // A file is staged if the index differs from HEAD
        // For deletions: stageStatus === 0 when deletion is staged (file removed from index)
        // For additions: stageStatus === 2 when addition is staged (index matches workdir)
        // For modifications: stageStatus === 2 when modification is staged (index matches workdir)
        const isStaged = headStatus !== stageStatus;

        // A file has unstaged changes if the working directory differs from the index
        // but only if the file is tracked (exists in HEAD or index)
        const isTracked = headStatus !== 0 || stageStatus !== 0;
        // For deletions: workdirStatus === 0 (absent) but stageStatus !== 0 (still in index)
        // For modifications/additions: workdirStatus === 2 (different/new) but stageStatus !== 2 (not staged)
        const hasUnstagedChanges =
          isTracked &&
          ((workdirStatus === 0 && stageStatus !== 0) || // Deleted in workdir but still in index
            (workdirStatus === 2 && stageStatus !== 2)); // Modified/added but not staged

        // A file is untracked if it's not in HEAD and not in the index, but exists in workdir
        // workdirStatus !== 0 means file exists in working directory
        const isUntracked =
          headStatus === 0 && stageStatus === 0 && workdirStatus !== 0;

        // Add to staged files if it has staged changes
        if (isStaged) {
          let status = "modified";
          if (headStatus === 0 && stageStatus !== 0) {
            // File doesn't exist in HEAD but exists in index - it's an addition
            status = "added";
          } else if (headStatus !== 0 && stageStatus === 0) {
            // File exists in HEAD but not in index - it's a staged deletion
            status = "deleted";
          } else if (headStatus !== 0 && stageStatus === 2) {
            // File exists in HEAD and index matches workdir - could be addition or modification
            // If workdirStatus === 2, it's a new file (addition)
            // If workdirStatus === 1, it's unmodified (shouldn't be here)
            status = workdirStatus === 2 ? "added" : "modified";
          } else if (headStatus !== 0 && stageStatus === 3) {
            // File exists in HEAD and index differs from workdir - modification
            status = "modified";
          }
          stagedFiles.push({
            path: filepath,
            status: "staged",
            originalStatus: status,
          });
        }

        // Add to unstaged files if it has unstaged changes
        if (hasUnstagedChanges) {
          let status = "modified";
          if (workdirStatus === 0 && stageStatus !== 0) {
            // File is deleted in working directory but still in index - unstaged deletion
            status = "deleted";
          } else if (workdirStatus === 2 && stageStatus !== 2) {
            // File is different in working directory but not staged
            // Check if it's an addition (doesn't exist in HEAD) or modification (exists in HEAD)
            if (headStatus === 0) {
              // File doesn't exist in HEAD - it's an addition
              status = "added";
            } else {
              // File exists in HEAD - it's a modification
              status = "modified";
            }
          }

          if (!isUntracked) {
            unstagedFiles.push({ path: filepath, status });
          }
        }

        // Add to untracked files
        if (isUntracked) {
          untrackedFiles.push({ path: filepath, status: "untracked" });
        }

        // Fallback check for missing files in status matrix
        if (
          !isStaged &&
          !hasUnstagedChanges &&
          !isUntracked &&
          headStatus === 0 &&
          workdirStatus === 0 &&
          stageStatus === 0
        ) {
          // Check if file actually exists
          try {
            const stats = await fs.stat(`/${filepath}`);
            const isDir =
              typeof stats.isDirectory === "function"
                ? stats.isDirectory()
                : stats.type === "dir";
            if (!isDir) {
              untrackedFiles.push({ path: filepath, status: "untracked" });
            }
          } catch (error) {
            // File doesn't exist, skip
          }
        }
      }

      // Also check for completely untracked files (not in status matrix)
      const allFiles = await this.getAllFiles(dir, fs);
      const trackedFiles = new Set(statusMatrix.map(([filepath]) => filepath));

      for (const filepath of allFiles) {
        if (
          !trackedFiles.has(filepath) &&
          !filepath.startsWith(".git/") &&
          filepath !== ".git" &&
          !filepath.startsWith(".revolt/") &&
          filepath !== ".revolt"
        ) {
          // Check if it's not already in untrackedFiles
          if (!untrackedFiles.find((f) => f.path === filepath)) {
            untrackedFiles.push({ path: filepath, status: "untracked" });
          }
        }
      }

      const allChangedFiles = [
        ...stagedFiles,
        ...unstagedFiles,
        ...untrackedFiles,
      ];

      logger.log(
        {
          tags: "tools|gitstatus|info",
          color1: "lime",
        },
        "Git status retrieved",
        {
          stagedCount: stagedFiles.length,
          unstagedCount: unstagedFiles.length,
          untrackedCount: untrackedFiles.length,
        }
      );

      return this.returnSuccess(
        {
          hasGit: true,
          stagedFiles,
          unstagedFiles,
          untrackedFiles,
          allFiles: allChangedFiles,
        },
        "Git status retrieved successfully"
      );
    } catch (error) {
      // Handle ENOENT errors gracefully during bulk operations (e.g., directory tree deletions)
      // When entire directory trees are deleted, git status may try to stat files that no longer exist
      if (error.code === 'ENOENT' || error.message?.includes('ENOENT')) {
        logger.warn(
          {
            tags: "tools|gitstatus|enoent",
            color1: "yellow",
          },
          "Git status encountered missing files during bulk operation, returning empty status",
          { error: error.message }
        );

        // Return safe fallback status - treat as if git status is temporarily unavailable
        return this.returnSuccess(
          {
            hasGit: true, // Git exists but status is temporarily unavailable
            stagedFiles: [],
            unstagedFiles: [],
            untrackedFiles: [],
            allFiles: [],
          },
          "Git status temporarily unavailable due to recent bulk operations"
        );
      }

      // For other errors, still log and return error
      logger.error(
        {
          tags: "tools|gitstatus|error",
          color1: "red",
        },
        "Failed to get git status",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Execute git status for local apps via Revolt Desktop IPC (spawns real `git`).
   * Shape matches `gitStatusForBrowser` so `GitStateManager.refreshGitState`
   * consumes it without changes. Surfaces `gitNotInstalled` distinctly so the
   * UI can tell the user to install Git instead of showing a generic error.
   */
  async gitStatusForLocal() {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitStatus) {
      return this.returnError(
        {},
        "Local git status requires Revolt Desktop (workspaceLocalAppAPI.gitStatus)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const res = await api.gitStatus({ rootPath: root });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        logger.error(
          { tags: "tools|gitstatus|local|git-missing", color1: "red" },
          "Git is not installed on this machine"
        );
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to get git status");
    }

    if (!res.hasGit) {
      return this.returnSuccess(
        {
          hasGit: false,
          stagedFiles: [],
          unstagedFiles: [],
          untrackedFiles: [],
          allFiles: [],
        },
        "No git repository found"
      );
    }

    logger.log(
      { tags: "tools|gitstatus|local|info", color1: "lime" },
      "Git status retrieved (local)",
      {
        stagedCount: res.stagedFiles?.length || 0,
        unstagedCount: res.unstagedFiles?.length || 0,
        untrackedCount: res.untrackedFiles?.length || 0,
      }
    );

    return this.returnSuccess(
      {
        hasGit: true,
        stagedFiles: res.stagedFiles || [],
        unstagedFiles: res.unstagedFiles || [],
        untrackedFiles: res.untrackedFiles || [],
        allFiles: res.allFiles || [
          ...(res.stagedFiles || []),
          ...(res.unstagedFiles || []),
          ...(res.untrackedFiles || []),
        ],
      },
      "Git status retrieved successfully"
    );
  }

  /**
   * Check if .git directory exists
   */
  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      return false;
    }
  }

  /**
   * Recursively get all files in the filesystem
   */
  async getAllFiles(dir, fs, basePath = "") {
    const files = [];

    try {
      const entries = await fs.readdir(dir === "/" ? "/" : dir);

      for (const entry of entries) {
        // Skip .git and .revolt directories
        if (entry === ".git" || entry === ".revolt") {
          continue;
        }

        const fullPath = dir === "/" ? `/${entry}` : `${dir}/${entry}`;
        const relativePath = basePath ? `${basePath}/${entry}` : entry;

        try {
          const stats = await fs.stat(fullPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            const subFiles = await this.getAllFiles(fullPath, fs, relativePath);
            files.push(...subFiles);
          } else {
            files.push(relativePath);
          }
        } catch (error) {
          // Skip files we can't stat
        }
      }
    } catch (error) {
      // Skip directories we can't read
    }

    return files;
  }
}
